/* eslint-disable no-unused-expressions */
import type { ReactNode } from 'react';
import type { IAlertProps } from '../../Components/util/alert/Alert';
import type { IConfirmationProps } from '../../Components/util/confirmation/Confirmation';

export enum DialogTypes {
  Confirmation,
  Alert,
}

export type DialogContent = ReactNode;

export interface IDialogOptions {
  closeOnDimerClick?: boolean;
  closeIcon?: boolean;
  children?: ReactNode;
  isAlert?: boolean;
  dialogId: Id;
}

export interface IConfirmationDialogProps
  extends Partial<Omit<IConfirmationProps, 'open'>>,
    IDialogOptions {
  dialogId: Id;
  onConfirm(): void;
  onRefuse(): void;
  acceptText?: string | ReactNode;
  refuseText?: string | ReactNode;
}

export interface IAlertDialogProps
  extends Partial<Omit<IAlertProps, 'open'>>,
    IDialogOptions {
  dialogId: Id;
  onConfirm(): void;
  confirmText?: string | ReactNode;
}

export type Id = number | string;

export interface IDialog {
  content: DialogContent;
  props: IDialogOptions;
  type: DialogTypes;
}

export const enum DialogEvent {
  Show,
  Close,
}

type OnShowCallback = (
  type: DialogTypes,
  content: DialogContent,
  options: IConfirmationDialogProps,
) => void;
type OnCloseCallback = (id: Id, isAccepted: boolean) => void;

type Callback = OnShowCallback | OnCloseCallback;

type TimeoutId = ReturnType<typeof setTimeout>;

interface DialogEventManager {
  list: Map<DialogEvent, Callback[]>;
  emitQueue: Map<DialogEvent, TimeoutId[]>;
  on(event: DialogEvent.Show, callback: OnShowCallback): DialogEventManager;
  on(event: DialogEvent.Close, callback: OnCloseCallback): DialogEventManager;
  off(event: DialogEvent, callback?: Callback): DialogEventManager;
  cancelEmit(event: DialogEvent): DialogEventManager;
  emit(
    event: DialogEvent.Show,
    type: DialogTypes.Confirmation,
    content: ReactNode,
  ): void;
  emit(
    event: DialogEvent.Show,
    type: DialogTypes.Confirmation,
    content: ReactNode,
    options: Partial<IConfirmationDialogProps>,
  ): void;
  emit(
    event: DialogEvent.Show,
    type: DialogTypes.Alert,
    content: ReactNode,
  ): void;
  emit(
    event: DialogEvent.Show,
    type: DialogTypes.Alert,
    content: ReactNode,
    options: Partial<IAlertDialogProps>,
  ): void;
  emit(
    event: DialogEvent.Close,
    id: string | number,
    isAccepted: boolean,
  ): void;
}

export const dialogEventManager: DialogEventManager = {
  list: new Map(),
  emitQueue: new Map(),

  on(event: DialogEvent, callback: Callback) {
    this.list.has(event) || this.list.set(event, []);
    this.list.get(event)!.push(callback);
    return this;
  },

  off(event, callback) {
    if (callback) {
      const cb = this.list.get(event)!.filter(cb => cb !== callback);
      this.list.set(event, cb);
      return this;
    }
    this.list.delete(event);
    return this;
  },

  cancelEmit(event) {
    const timers = this.emitQueue.get(event);
    if (timers) {
      timers.forEach(clearTimeout);
      this.emitQueue.delete(event);
    }

    return this;
  },

  /**
   * Enqueue the event at the end of the call stack
   * Doing so let the user call Dialog as follow:
   * Dialog('1')
   * Dialog('2')
   * Dialog('3')
   * Without setTimemout the code above will not work
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit(event: DialogEvent, ...args: any[]) {
    this.list.has(event) &&
      this.list.get(event)!.forEach((callback: Callback) => {
        const timer: TimeoutId = setTimeout(() => {
          // @ts-ignore
          callback(...args);
        }, 0);

        this.emitQueue.has(event) || this.emitQueue.set(event, []);
        this.emitQueue.get(event)!.push(timer);
      });
  },
};
